import { Empty } from "@baseline/common/types";
import { DefaultEventEmitter } from "@baseline/runtime/events";
import { Context } from "./context";
import { Request } from "./request";
import { RoutePath, RoutePathArguments } from "./route-path";
import { Router } from "./router";


export class RoutingManager extends DefaultEventEmitter<Event>
{
    constructor()
    {
        super();
        this.route = this.route.bind(this);
        this.refresh = this.refresh.bind(this);
    }

    addRouter(router: Router): this
    {
        this.routers.push(router);
        return this;
    }

    route(path: string, options?: RouteOptions & { args?: any; }): Promise<boolean>;
    route<T extends Empty>(routPath: RoutePath<T>, options?: RouteOptions): Promise<boolean>;
    route<T extends RoutePathArguments>(routPath: RoutePath<T>, options: RouteOptions & { args: T; }): Promise<boolean>;
    async route(routeArg: string | RoutePath, options?: RouteOptions & { args?: any; }): Promise<boolean>
    {
        const { args } = options ?? {};

        // Resolve requested path
        let path =
            (typeof routeArg == "string")
                ? routeArg
                : routeArg.make(args);
        path = path || "/";

        // Update shown path if changed
        const currentPath = window.location.pathname;
        if (path != currentPath)
        {
            if (options?.replace)
                window.history.replaceState({}, "", path);
            else
                window.history.pushState({}, "", path);
        }

        // Route
        if (path != this.path || options?.force)
        {
            // Update path
            var previous = this.path;
            this.path = path;

            // Emit event
            if (this.path != previous)
                this.emit("changed", { previous, path });

            // Route
            const handled =
                await Promise.all
                    (
                        this.routers.map(
                            router =>
                            {
                                const context = this.createContext(path);
                                return router.route(context);
                            }
                        )
                    );
            return handled.some(item => item);
        }
        else
        {
            return true;
        }
    }

    start(): void
    {
        window.addEventListener("hashchange", () => this.refresh());
        window.addEventListener("popstate", () => this.refresh());
        this.refresh();
    }

    async refresh(force?: boolean): Promise<void>
    {
        await this.route(window.location.pathname, { force, replace: true });
    };

    path: string;


    protected createContext(path: string): Context
    {
        const segments = path.split('/').filter(segment => !!segment);
        const requestPath = segments.join('/');
        const request: Request = { path, segments, requestPath, requestSegments: segments };
        const context = { request };
        return context;
    }


    protected routers: Router[] = [];
}

export interface RoutingManager
{
    on(event: "changed", handler: (args: PathChangedEventArgs) => void): this;
    off(event: "changed", handler: (args: PathChangedEventArgs) => void): this;
    emit(event: "changed", eventArgs: PathChangedEventArgs): this;
}


export interface RouteOptions
{
    force?: boolean;
    replace?: boolean;
};


export type Event = "changed";


export interface PathChangedEventArgs
{
    previous: string;
    path: string;
}
